import React, { useEffect, useState } from 'react';
import parlamentarService from '../services/parlamentarLimitesService';
import councilorService, { CouncilorDTO } from '../services/councilorService';
import { UI_THEME } from '../ui/theme';
import { useAuth } from '../context/AuthContext';
import { Permission } from '../types/user.types';
import { notifyError } from '../utils/notify';

const emptyForm = () => ({
  parlamentarId: '',
  ano: new Date().getFullYear(),
  valorAnual: '',
});

const AdminParlamentarLimitesPage: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [parlamentares, setParlamentares] = useState<CouncilorDTO[]>([]);
  const [limitsLoading, setLimitsLoading] = useState(true);
  const [councilorLoading, setCouncilorLoading] = useState(true);
  const [councilorError, setCouncilorError] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [filtroAno, setFiltroAno] = useState('');

  const { hasPermission } = useAuth();
  const canManage = hasPermission(Permission.MANAGE_PARLAMENTAR_LIMITES);

  const isSaas = UI_THEME === 'saas';
  const surfaceClass = 'border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80';
  const heroClass = isSaas
    ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/70 to-indigo-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
    : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800';
  const accentTextClass = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const bodyTextClass = 'text-slate-600 dark:text-slate-300';
  const labelTextClass = 'text-slate-500 dark:text-slate-400';
  const strongTextClass = 'text-slate-900 dark:text-slate-100';
  const inputClass = 'w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:disabled:bg-slate-800/60 dark:focus:ring-blue-900/40';
  const primaryButtonClass = isSaas
    ? 'bg-teal-600 text-white hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-400'
    : 'bg-blue-700 text-white hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-500';
  const secondaryButtonClass = 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';

  const formatCurrency = (value: string | number) => {
    const raw = String(value ?? '').replace(/\D/g, '');
    if (!raw) return '';
    const numberValue = Number(raw) / 100;
    return numberValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const parseCurrencyToNumber = (value: string) => {
    const raw = value.replace(/[^\d]/g, '');
    if (!raw) return 0;
    return Number(raw) / 100;
  };

  const handleValorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = formatCurrency(e.target.value);
    setForm(f => ({ ...f, valorAnual: masked }));
  };

  const loadCouncilors = async () => {
    setCouncilorLoading(true);
    setCouncilorError(null);
    try {
      const pcs = await councilorService.list();
      setParlamentares(Array.isArray(pcs) ? pcs : (pcs?.data || []));
    } catch (err) {
      console.error(err);
      setCouncilorError('Erro ao carregar parlamentares');
    } finally {
      setCouncilorLoading(false);
    }
  };

  const loadLimites = async () => {
    setLimitsLoading(true);
    try {
      const res = await parlamentarService.listAll();
      setList(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLimitsLoading(false);
    }
  };

  useEffect(() => {
    loadCouncilors();
    loadLimites();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await parlamentarService.create({
        parlamentarId: form.parlamentarId,
        ano: Number(form.ano),
        valorAnual: parseCurrencyToNumber(form.valorAnual),
      });
      await loadLimites();
      setForm(emptyForm());
    } catch (err: any) {
      notifyError(err?.response?.data?.error || err.message || 'Erro');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Confirma exclusão?')) return;
    try {
      await parlamentarService.remove(id);
      setList(prev => prev.filter(p => p.id !== id));
    } catch {
      notifyError('Erro ao excluir');
    }
  };

  const startEdit = (item: any) => {
    setEditingId(item.id);
    setForm({ parlamentarId: item.parlamentarId, ano: item.ano, valorAnual: formatCurrency(item.valorAnual) });
  };

  const resetEdit = () => {
    setEditingId(null);
    setForm(emptyForm());
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId) return;
    try {
      await parlamentarService.update(editingId, { valorAnual: parseCurrencyToNumber(form.valorAnual) });
      await loadLimites();
      resetEdit();
    } catch (err: any) {
      notifyError(err?.response?.data?.error || err.message || 'Erro');
    }
  };

  // Limites aplicam-se somente a parlamentares municipais (estadual/federal ficam de fora).
  const parlamentaresMunicipais = parlamentares.filter((p) => !p.esfera || p.esfera === 'MUNICIPAL');

  const anosDisponiveis = Array.from(new Set(list.map(l => l.ano))).sort((a, b) => b - a);
  const listFiltrada = filtroAno === '' ? list : list.filter(l => String(l.ano) === filtroAno);

  const totalAnual = list.reduce((sum, item) => sum + Number(item.valorAnual || 0), 0);
  const summaryCards = [
    { label: 'Limites cadastrados', value: list.length, dot: 'bg-slate-400' },
    { label: 'Parlamentares', value: parlamentares.length, dot: isSaas ? 'bg-teal-500' : 'bg-blue-600' },
    { label: 'Ano atual', value: new Date().getFullYear(), dot: 'bg-amber-500' },
    { label: 'Total configurado', value: totalAnual.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), dot: 'bg-emerald-500' },
  ];

  return (
    <div className="w-full space-y-6">
      <section className={`rounded-3xl border ${heroClass}`}>
        <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${accentTextClass}`}>
          {isSaas ? 'Regras financeiras' : 'Administrador'}
        </p>
        <h1 className={`mt-2 text-2xl font-bold tracking-tight sm:text-3xl ${strongTextClass}`}>Limites anuais por parlamentar</h1>
        <p className={`mt-2 text-sm leading-6 ${bodyTextClass}`}>
          Controle os limites anuais de indicação para cada parlamentar.
        </p>

        <div className={`mt-5 overflow-hidden rounded-2xl border ${surfaceClass}`}>
          <div className="grid divide-y divide-slate-200 dark:divide-slate-700 sm:grid-cols-2 sm:divide-x sm:divide-y-0 xl:grid-cols-4">
            {summaryCards.map((card) => (
              <div key={card.label} className="min-h-[72px] px-4 py-3">
                <p className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] ${labelTextClass}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${card.dot}`} />
                  {card.label}
                </p>
                <p className={`mt-2 text-2xl font-bold ${strongTextClass}`}>{card.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {canManage && <section className={`rounded-3xl border p-5 ${surfaceClass}`}>
        <form onSubmit={editingId ? handleUpdate : handleCreate} className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(260px,1fr)_140px_180px_auto] md:items-end">
          <div>
            <label className={`mb-1 block text-xs font-semibold uppercase tracking-[0.12em] ${labelTextClass}`}>Parlamentar</label>
            <select
              required
              disabled={councilorLoading || !!councilorError || !!editingId}
              value={form.parlamentarId}
              onChange={e => setForm(f => ({ ...f, parlamentarId: e.target.value }))}
              className={inputClass}
            >
              <option value="">{councilorLoading ? 'Carregando parlamentares...' : 'Selecione um parlamentar'}</option>
              {!councilorLoading && councilorError && <option disabled>{councilorError}</option>}
              {!councilorLoading && !councilorError && parlamentaresMunicipais.length === 0 && <option disabled>Nenhum parlamentar municipal encontrado</option>}
              {parlamentaresMunicipais.map((p) => <option key={p.councilorId} value={p.councilorId}>{p.fullName}</option>)}
            </select>
          </div>
          <div>
            <label className={`mb-1 block text-xs font-semibold uppercase tracking-[0.12em] ${labelTextClass}`}>Ano</label>
            <input
              type="number"
              disabled={!!editingId}
              value={form.ano}
              onChange={e => setForm(f => ({ ...f, ano: Number(e.target.value) }))}
              className={inputClass}
            />
          </div>
          <div>
            <label className={`mb-1 block text-xs font-semibold uppercase tracking-[0.12em] ${labelTextClass}`}>Valor anual</label>
            <input
              type="text"
              inputMode="decimal"
              value={form.valorAnual}
              onChange={handleValorChange}
              className={inputClass}
            />
          </div>
          <div className="flex gap-2">
            <button type="submit" className={`rounded-xl px-4 py-2.5 text-sm font-semibold transition ${primaryButtonClass}`}>
              {editingId ? 'Salvar' : 'Adicionar'}
            </button>
            {editingId && (
              <button type="button" onClick={resetEdit} className={`rounded-xl px-4 py-2.5 text-sm font-medium transition ${secondaryButtonClass}`}>
                Cancelar
              </button>
            )}
          </div>
          <p className={`text-xs md:col-span-4 ${labelTextClass}`}>Somente parlamentares municipais possuem limite anual.</p>
        </form>
      </section>}

      <section className={`overflow-hidden rounded-3xl border ${surfaceClass}`}>
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <label className={`text-xs font-semibold uppercase tracking-[0.12em] ${labelTextClass}`}>Exercício</label>
          <select
            value={filtroAno}
            onChange={e => setFiltroAno(e.target.value)}
            className="rounded-xl border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-blue-900/40"
          >
            <option value="">Todos os exercícios</option>
            {anosDisponiveis.map(ano => <option key={ano} value={String(ano)}>{ano}</option>)}
          </select>
        </div>
        {limitsLoading ? <div className={`p-6 text-sm ${bodyTextClass}`}>Carregando...</div> : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
                <tr>
                  <th className={`px-4 py-3 text-left font-semibold ${bodyTextClass}`}>Parlamentar</th>
                  <th className={`px-4 py-3 text-left font-semibold ${bodyTextClass}`}>Ano</th>
                  <th className={`px-4 py-3 text-left font-semibold ${bodyTextClass}`}>Valor anual</th>
                  {canManage && <th className={`px-4 py-3 text-right font-semibold ${bodyTextClass}`}>Ações</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {listFiltrada.length === 0 && (
                  <tr>
                    <td colSpan={canManage ? 4 : 3} className={`px-4 py-8 text-center text-sm ${labelTextClass}`}>{filtroAno === '' ? 'Nenhum limite cadastrado.' : `Nenhum limite cadastrado para o exercício ${filtroAno}.`}</td>
                  </tr>
                )}
                {listFiltrada.map(l => (
                  <tr key={l.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/60">
                    <td className={`px-4 py-3 ${strongTextClass}`}>{parlamentares.find(p => p.councilorId === l.parlamentarId)?.fullName || l.parlamentarId}</td>
                    <td className={`px-4 py-3 ${bodyTextClass}`}>{l.ano}</td>
                    <td className={`px-4 py-3 font-medium ${strongTextClass}`}>{Number(l.valorAnual).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                    <td className="px-4 py-3 text-right">
                      {canManage && <button type="button" onClick={() => startEdit(l)} className={`mr-3 text-sm font-medium ${accentTextClass}`}>Editar</button>}
                      {canManage && <button type="button" onClick={() => handleDelete(l.id)} className="text-sm font-medium text-red-700 hover:text-red-900 dark:text-red-300 dark:hover:text-red-200">Excluir</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminParlamentarLimitesPage;
